import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { projectApi } from '@/api';
import { QUERY_KEY_CONST, QUERY_META_CONST } from '@/constant';
import { useTypedSearchParams } from '@/hook/searchParams';
import { useVToast } from '@/package/kiska/src/component/UI/VToast';
import type {
  Project,
  ProjectApi,
  ProjectSearchParams,
  ProjectState,
  User,
} from '@/type';
import { ERROR_CODE } from '@/util';

export const useProjectDetailQuery = (projectId: Project['id']) => {
  return useQuery({
    queryFn: () =>
      projectApi
        .getDetail({ pProjectID: projectId })
        .then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getDetail(projectId),
  });
};

export const useProejctListQuery = (params?: {
  workerID?: User['id'];
  reviewerID?: User['id'];
}) => {
  const { get, getAll } = useTypedSearchParams<ProjectSearchParams>();

  const name = get('projectName');
  const limit = Number(get('projectLimit'));
  const offset = Number(get('projectOffset'));
  const keyword = get('projectKeyword');
  const state = getAll('projectState');

  const listParams: ProjectApi.GetList.Request = {
    limit,
    offset,
    name,
    keyword,
    state: state.length ? (state as ProjectState[]) : null,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value === null) delete listParams[key as keyof typeof listParams];
  });

  if (params?.workerID) listParams['workerID'] = params.workerID;
  if (params?.reviewerID) listParams['reviewerID'] = params.reviewerID;

  return useQuery({
    queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getList(listParams),
    queryFn: () =>
      projectApi.getList(listParams).then(res => res.data.resultData),
  });
};

export const useInfiniteProejctListQuery = (params?: {
  workerID?: User['id'];
  reviewerID?: User['id'];
}) => {
  const { get, getAll } = useTypedSearchParams<ProjectSearchParams>();

  const limit = get('projectLimit') ? Number(get('projectLimit')) : 50;
  const keyword = get('projectKeyword');
  const state = getAll('projectState');

  const listParams: ProjectApi.GetList.Request = {
    limit,
    keyword,
    state: state.length ? (state as ProjectState[]) : null,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value === null) delete listParams[key as keyof typeof listParams];
  });

  if (params?.workerID) listParams['workerID'] = params.workerID;
  if (params?.reviewerID) listParams['reviewerID'] = params.reviewerID;

  return useInfiniteQuery({
    queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getList(listParams),
    queryFn: ({ pageParam }) =>
      projectApi.getList({ ...listParams, cursor: pageParam }),
    initialPageParam: '',
    getNextPageParam: nextPage => nextPage.data.cursor,
  });
};

export const useProjectCountQuery = ({ user }: { user?: User }) => {
  const { get, getAll } = useTypedSearchParams<ProjectSearchParams>();
  const name = get('projectName');
  const keyword = get('projectKeyword');
  const state = getAll('projectState');

  const listParams: ProjectApi.GetCount.Request = {
    name,
    keyword,
    state: state.length ? (state as ProjectState[]) : null,
  };

  if (user) listParams['userID'] = user.id;

  Object.entries(listParams).forEach(([key, value]) => {
    if (value === null) delete listParams[key as keyof typeof listParams];
  });

  return useQuery({
    queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getCount(listParams),
    queryFn: () =>
      projectApi.getCount(listParams).then(res => res.data.resultData),
  });
};

export const useProjectCreateQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ProjectApi.Create.Request) =>
      projectApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TASK_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getCount(),
      });
    },
  });
};

export const useProjectRemoveQuery = () => {
  const { t } = useTranslation();
  const { addToast } = useVToast();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectID: Project['id']) => {
      return projectApi.remove({
        pProjectID: projectID,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getCount(),
      });
    },
    onError: error => {
      if ((error as any).data.resultCode === ERROR_CODE['1403']) {
        addToast({
          variant: 'error',
          message: t('error.modelDependencyError.corectome'),
          autoClose: true,
        });
      }
    },
    meta: {
      [QUERY_META_CONST.META_KEY.OVERRIDE_GLOBAL_ERROR_HANDLER]: true,
    },
  });
};

export const useProjectImportQuery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: ProjectApi.Import.Request) =>
      projectApi.importProject(params).then(res => res.data.resultData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getList(),
      });
    },
  });
};

export const useProjectUpdateQuery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: ProjectApi.Update.Request) =>
      projectApi.update(params).then(res => res.data.resultData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.PROJECT_KEYS.getDetail(),
      });
    },
  });
};
